import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    AlertTitle,
    Box,
    CircularProgress,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import ReadNotification from "./ReadNotification";
import { errorAlert, handleSuccess, updateAlert } from "../../Components/Alerts";
import CancelModal from "../../Components/CancelModal";

export default function Notification() {
    const [formData, setFormData] = useState({
        title: "",
        message: "",
        version: "",
        visibleTo: "",
        isActive: true,
    });

    const [notificationID, setNotificationID] = useState("")
    const [isInsertForm, setIsInsertForm] = useState(true);
    const [showEditBtn, setShowEditBtn] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [openInsertForm, setOpenInsertForm] = useState(false);
    const [showCloseBtn, setShowCloseBtn] = useState(false);

    const [role, setRole] = useState("");
    const canManage = role === "acctree";

    const handleEdit = (notification) => {
        if (!canManage) return;
        setFormData({
            title: notification.Title,
            message: notification.Message,
            version: notification.Version,
            visibleTo: notification.VisibleTo,
            isActive: notification.IsActive
        });
        setNotificationID(notification.NotificationID);
        setIsInsertForm(false);
        setOpenInsertForm(true);
        setShowCloseBtn(true);
    }

    const [getData, setGetData] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const headers = authHeader();

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canManage) return;

        const action = e.nativeEvent.submitter?.value;

        // Validimi i fushave të domosdoshme
        const isFormValid = () =>
            formData.title && formData.message && formData.version && formData.visibleTo;

        if (!isFormValid()) {
            errorAlert("Ju lutem plotësoni të gjitha fushat e detyrueshme.");
            return;
        }

        setIsSubmitting(true);

        try {
            if (action === "edit") {
                const notificationData = {
                    ...formData,
                    NotificationID: notificationID,
                };

                await axios.put(
                    `${ApiLink.API_URL}Notification/updateNotification`,
                    notificationData,
                    { headers }
                );

                updateAlert("Njoftimi u modifikua me sukses");
            } else {
                await axios.post(
                    `${ApiLink.API_URL}Notification`,
                    formData,
                    { headers }
                );

                handleSuccess("Njoftimi u krijua me sukses!");
            }

            // ✅ Logjika që duhet të ekzekutohet pas suksesit (për të dy rastet)
            setFormData({
                title: "",
                message: "",
                version: "",
                visibleTo: "",
                isActive: true,
            });

            setIsInsertForm(true);     // Nëse ke formë të veçantë për insert
            setGetData(true);          // Rifresko të dhënat (map-i)

        } catch (error) {
            console.error(error);
            errorAlert("Gabim gjatë përpunimit të njoftimit.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const openForm = () => {
        if (!canManage) return;
        setOpenInsertForm(true);
        setShowCloseBtn(true);
    }
    const closeForm = () => {
        setOpenInsertForm(false);
        setShowCloseBtn(false);
        clearFields();
    }

    const fetchRole = useCallback(async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + 'AuthProva/getrole', { headers });

            if (response.data.UserRole && response.data.UserRole !== undefined) {
                setRole(response.data.UserRole);
            } else {
                errorAlert("Undefined Role.");
            }
        } catch (error) {
            console.error("Error fetching role:", error);
        }
    }, [headers]);

    const clearFields = () => {
        setFormData({
            title: "",
            message: "",
            version: "",
            visibleTo: "",
            isActive: true
        });
        setNotificationID("");
        setIsInsertForm(true);
        setShowModal(false);
        setShowEditBtn(false);
    }

    useEffect(() => {
        fetchRole();
    }, [fetchRole]);
    return (
        <div style={{ paddingTop: "50px" }}>
            <CancelModal
                open={canManage && showModal}
                onClose={() => setShowModal(false)}
                title="Konfirmim"
                description="Jeni të sigurt që dëshironi ti anuloni ndryshimet e bera?"
                confirmText="Po"
                cancelText="Jo"
                onConfirm={() => {
                    clearFields();
                    setOpenInsertForm(false);
                    setShowCloseBtn(false);
                }}
            />

            <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, pb: 6 }}>
                {canManage && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                        <Button type="button" variant={showCloseBtn ? "outlined" : "contained"}
                            startIcon={showCloseBtn ? undefined : <AddIcon />}
                            onClick={showCloseBtn ? closeForm : openForm}
                            sx={{ borderRadius: 2, textTransform: "none", boxShadow: "none" }}>
                            {showCloseBtn ? "Mbyll formën" : "Krijo njoftim të ri"}
                        </Button>
                    </Box>
                )}

                {canManage && openInsertForm ? (
                    <Box sx={{ mb: 4 }}>
                        <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: "none", bgcolor: "background.paper" }}>
                            <CardHeader
                                avatar={isInsertForm ? <AddIcon color="primary" /> : <EditIcon color="primary" />}
                                title={isInsertForm ? "Krijo njoftim" : "Modifiko njoftimin"}
                                subheader="Plotësoni përmbajtjen dhe zgjidhni audiencën e njoftimit."
                                titleTypographyProps={{ variant: "h6", component: "h2", fontWeight: 700 }}
                                sx={{ px: { xs: 2, md: 3 }, pt: 3, pb: 0 }}
                            />
                            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                <form onSubmit={handleSubmit} noValidate>
                                    <TextField
                                        label="Titulli"
                                        required
                                        fullWidth
                                        margin="normal"
                                        size="small"
                                        value={formData.title}
                                        onChange={(e) => { handleInputChange("title", e.target.value); setShowEditBtn(true); }}
                                        error={Boolean(errors.title)}
                                        helperText={errors.title}
                                        autoComplete="off"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor: "background.default",
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Përmbajtja"
                                        required
                                        fullWidth
                                        margin="normal"
                                        multiline
                                        minRows={4}
                                        size="small"
                                        value={formData.message}
                                        onChange={(e) => { handleInputChange("message", e.target.value); setShowEditBtn(true); }}
                                        error={Boolean(errors.message)}
                                        helperText={errors.message}
                                        autoComplete="off"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor: "background.default",
                                            },
                                        }}
                                    />

                                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2, mt: 2, alignItems: "start" }}>
                                        <TextField
                                            label="Versioni"
                                            fullWidth
                                            size="small"
                                            value={formData.version}
                                            onChange={(e) => { handleInputChange("version", e.target.value); setShowEditBtn(true); }}
                                            autoComplete="off"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    backgroundColor: "background.default",
                                                },
                                            }}
                                        />

                                    <FormControl
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors.visibleTo)}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor: "background.default",
                                            },
                                        }}
                                    >
                                        <InputLabel id="visibleTo-label">Audienca *</InputLabel>
                                        <Select
                                            labelId="visibleTo-label"
                                            value={formData.visibleTo}
                                            label="Audienca *"
                                            onChange={(e) => { handleInputChange("visibleTo", e.target.value); setShowEditBtn(true); }}
                                        >
                                            <MenuItem value="All">Të gjithë përdoruesit</MenuItem>
                                            <MenuItem value="Admins">Administratorët</MenuItem>
                                            <MenuItem value="Staff">Stafi</MenuItem>
                                            <MenuItem value="Custom">Kompani e caktuar</MenuItem>
                                        </Select>
                                        {errors.visibleTo && (
                                            <Typography color="error" variant="caption" mt={0.5}>
                                                {errors.visibleTo}
                                            </Typography>
                                        )}
                                    </FormControl>
                                    </Box>

                                    {(formData.title || formData.message) && (
                                        <Alert
                                            severity="info"
                                            sx={{
                                                mt: 3,
                                                borderRadius: 2,
                                                border: "1px solid rgba(33,150,243,0.18)",
                                            }}
                                        >
                                            <AlertTitle>
                                                <NotificationsIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 1 }} />
                                                Parapamje
                                            </AlertTitle>
                                            <Typography variant="subtitle1" fontWeight="700" sx={{ color: "text.primary" }}>
                                                {formData.title || "Titulli i njoftimit"}
                                            </Typography>
                                            <Typography variant="body2" mb={1}>
                                                {formData.message || "Përmbajtja e njoftimit do të shfaqet këtu"}
                                            </Typography>
                                            <Box display="flex" gap={1} flexWrap="wrap" fontSize="0.8rem" color="text.secondary">
                                                {formData.version && (
                                                    <Chip label={`v${formData.version}`} size="small" variant="outlined" />
                                                )}
                                                {formData.visibleTo && (
                                                    <Typography component="span" sx={{ ml: 1 }}>
                                                        Audienca:{" "}
                                                        {({ All: "Të gjithë përdoruesit", Admins: "Administratorët", Staff: "Stafi", Custom: "Kompani e caktuar" })[formData.visibleTo]}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Alert>
                                    )}

                                    {isInsertForm ? (
                                        <Box display="flex" gap={2} mt={4} flexDirection={{ xs: "column", sm: "row" }}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                name="action"
                                                value="insert"
                                                disabled={isSubmitting}
                                                startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                                                fullWidth
                                                sx={{
                                                    borderRadius: 2,
                                                    py: 1.25,
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    bgcolor: "primary.main",
                                                    "&:hover": { bgcolor: "primary.dark" },
                                                }}
                                            >
                                                {isSubmitting ? "Duke u ruajtur…" : "Ruaj njoftimin"}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                type="button"
                                                sx={{
                                                    borderRadius: 2,
                                                    py: 1.25,
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    borderColor: "divider",
                                                    color: "text.primary",
                                                    "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                                                }}
                                                onClick={() => {
                                                    clearFields();
                                                }}
                                            >
                                                Pastro fushat
                                            </Button>
                                        </Box>
                                    ) : showEditBtn ? (
                                        <Box display="flex" gap={2} mt={4} flexDirection={{ xs: "column", sm: "row" }}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                name="action"
                                                value="edit"
                                                disabled={isSubmitting}
                                                startIcon={isSubmitting ? <CircularProgress size={20} /> : <EditIcon />}
                                                fullWidth
                                                sx={{
                                                    borderRadius: 2,
                                                    py: 1.25,
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    bgcolor: "primary.main",
                                                    "&:hover": { bgcolor: "primary.dark" },
                                                }}
                                            >
                                                {isSubmitting ? "Duke u ruajtur…" : "Ruaj ndryshimet"}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    borderRadius: 2,
                                                    py: 1.25,
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    borderColor: "divider",
                                                    color: "text.primary",
                                                    "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                                                }}
                                                onClick={() => {
                                                    setShowModal(true);
                                                }}
                                            >
                                                Anulo ndryshimet
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Box display="flex" gap={2} mt={4}>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    borderRadius: 2,
                                                    py: 1.25,
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    borderColor: "divider",
                                                    color: "text.primary",
                                                    "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    closeForm();
                                                }}
                                            >
                                                Mbyll formën
                                            </Button>
                                        </Box>
                                    )}
                                </form>
                            </CardContent>
                        </Card>
                    </Box>
                ) : null}

                <ReadNotification getData={getData} canManage={canManage} onEdit={canManage ? handleEdit : undefined} />
            </Box>
        </div>
    );
}
